import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { getRouteApi } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';

import { APIupdatePassword } from '../../api/User';

import Notices from '../Notices';

import { setNotice } from '../../helpers/notice';

import type { PasswordFormData } from '../../types/forms/passwordform';

const Password: React.FC = () => {
  const routeApi = getRouteApi('/account');
  const { profile } = routeApi.useLoaderData();

  const { mutate: updatePassword, isPending } = useMutation({
    mutationFn: async (requestData: PasswordFormData): Promise<PasswordFormData> => {
      const response = await APIupdatePassword(profile.id, requestData);
      return response.data;
    },
    onError: (error) => {
      setNotice('update.failed', error.message);
    },
    onSuccess: () => {
      setNotice('password.updated');
      reset();
    },
  });

  const schema = z.object({
    current_password: z.string().min(1, 'Current password is required'),
    new_password: z.string().min(8, 'New password must be at least 8 characters long'),
    confirm_new_password: z.string().min(1, 'Please confirm your new password'),
  }).refine((data) => data.new_password === data.confirm_new_password, {
    message: 'Passwords do not match',
    path: ['confirm_new_password'],
  });

  const {
    register,
    formState: {
      errors,
    },
    reset,
    handleSubmit,
  } = useForm<PasswordFormData>({
    defaultValues: {
      current_password: '',
      new_password: '',
      confirm_new_password: '',
    },
    resolver: zodResolver(schema) as any,
  });

  const submit = (data: PasswordFormData) => {
    updatePassword(data);
  };

  return (
    <div className="container account-container account-profile-container">
      <div className="account-header page-header">
        <h2 className="page-title">My <em>profile</em></h2>
      </div>
      <div className="account-content account-profile-content">
        <Notices />
        <form onSubmit={handleSubmit(submit)} noValidate>
          <wa-input
            label="Current Password"
            type="password"
            disabled={isPending}
            className={errors.current_password ? 'invalid' : ''}
            hint={errors.current_password ? errors.current_password.message : ''}
            required
            {...register('current_password')}
          />
          <wa-input
            label="New Password"
            type="password"
            disabled={isPending}
            className={errors.new_password ? 'invalid' : ''}
            hint={errors.new_password ? errors.new_password.message : ''}
            required
            {...register('new_password')}
          />
          <wa-input
            label="Confirm New Password"
            type="password"
            disabled={isPending}
            className={errors.confirm_new_password ? 'invalid' : ''}
            hint={errors.confirm_new_password ? errors.confirm_new_password.message : ''}
            required
            {...register('confirm_new_password')}
          />
          <wa-button type="submit" disabled={isPending}>
            {isPending ? 'Saving...' : 'Save'}
          </wa-button>
        </form>
      </div>
    </div>
  );
}

export default Password;
